/**
 * The engines' names, and which engines a screen lists: one source for «Control» and for the
 * configuration's graph, so the two never call one engine by two names (product owner,
 * 2026-10-14).
 *
 * Six engines, in the owner's order, each with the name he gave it. The first three are the ones a
 * dataset video or a person's CV runs today (`ACTIVE_ENGINES`); the other three only ever ran on
 * the old interviews, and are shown locked («Deshabilitado») wherever they appear.
 *
 * «Preguntas del cliente» (`client_questions`) is still an engine of its own to the workers, with
 * its own version and changelog, but it was split out of an earlier engine and is not one the
 * screens list: it is shown inside «Motor de Análisis Pre Competencial» (`FOLDED_INTO`), with its
 * version, rather than as a seventh engine (owner, 2026-10-14).
 *
 * A leaf on purpose: it imports nothing, so the catalogue (`engine-rules.ts`), the changelog
 * (`engine-changelog.ts`) and the graph (`methodology-map.ts`) can all read it without a cycle.
 * (`engine-rules.ts` also has an `ENGINE_NAMES`, the engines' identifiers; this one is what they
 * are called.)
 */

/** The engines the screens list, in the owner's order. */
export const ENGINE_ORDER = Object.freeze([
  'gestures',
  'coherence',
  'cv',
  'cultural_fit',
  'pre_competency',
  'toolkit',
] as const);

export type ListedEngine = (typeof ENGINE_ORDER)[number];

/** What each engine is called wherever it is the subject: a tile, a node, a panel, a page. */
export const ENGINE_NAMES: Readonly<Record<ListedEngine, string>> = Object.freeze({
  gestures: 'Motor de Análisis de Gestos',
  coherence: 'Motor de Análisis de Veracidad',
  cv: 'Motor de Extracción de CV',
  cultural_fit: 'Motor de Análisis de Fit Cultural',
  pre_competency: 'Motor de Análisis Pre Competencial',
  toolkit: 'Motor de Toolkit Triple A',
});

/**
 * The short label, where the engine is one word among others: «Gestos 5.2.1» in an execution
 * line, the launch dialog's switches. The catalogue's labels read from here; «Fit Cultural» was
 * «Encaje cultural» until the owner renamed it (2026-10-14), and «Pre Competencial» and «Toolkit
 * Triple A» are spelled as in his names, so one engine is never spelled two ways across screens.
 */
export const ENGINE_SHORT_NAMES = Object.freeze({
  gestures: 'Gestos',
  coherence: 'Veracidad',
  cv: 'CV',
  cultural_fit: 'Fit Cultural',
  client_questions: 'Preguntas del cliente',
  pre_competency: 'Pre Competencial',
  toolkit: 'Toolkit Triple A',
} as const satisfies Record<string, string>);

const SHORT_NAMES: Readonly<Record<string, string>> = ENGINE_SHORT_NAMES;

/** The engines a dataset video or a CV runs today; the rest ran only on the old interviews. */
export const ACTIVE_ENGINES: readonly string[] = Object.freeze(['gestures', 'coherence', 'cv']);

/** Why a locked engine is locked, wherever it says so. */
export const DISABLED_ENGINE_NOTE = 'Motor de versiones anteriores: no se puede habilitar';

/** An engine shown as part of another, rather than on its own. */
export const FOLDED_INTO: Readonly<Record<string, ListedEngine>> = Object.freeze({
  client_questions: 'pre_competency',
});

/** The engine `engine` is shown inside; null when it is shown on its own. */
export function foldedInto(engine: string): ListedEngine | null {
  return FOLDED_INTO[engine] ?? null;
}

/** The engines shown inside `host`, in a stable order. */
export function foldedEngines(host: string): string[] {
  return Object.keys(FOLDED_INTO)
    .filter((engine) => FOLDED_INTO[engine] === host)
    .sort();
}

/** «speech_rate» reads «Speech rate»: an engine nobody named yet still reads as words. */
function humanised(engine: string): string {
  const words = engine.replaceAll(/[_-]+/g, ' ').trim();

  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : engine;
}

function isListed(engine: string): engine is ListedEngine {
  return (ENGINE_ORDER as readonly string[]).includes(engine);
}

/** The engine's full name; its short label, or its identifier as words, when it has none. */
export function engineName(engine: string): string {
  return isListed(engine) ? ENGINE_NAMES[engine] : engineShortName(engine);
}

/** The engine's short label, or its identifier as words. */
export function engineShortName(engine: string): string {
  return SHORT_NAMES[engine] ?? humanised(engine);
}

/** The owner's order first; an engine he did not list after them, alphabetically. */
export function byEngineOrder(a: string, b: string): number {
  const rank = (engine: string): number =>
    isListed(engine) ? ENGINE_ORDER.indexOf(engine) : ENGINE_ORDER.length;

  return rank(a) - rank(b) || a.localeCompare(b);
}

/**
 * The engines a screen lists: the six, and after them any other engine the workers report, so a
 * new one appears the day it is deployed rather than when somebody names it. Folded engines are
 * never listed on their own.
 */
export function listedEngines(reported?: Iterable<string> | null): string[] {
  const extra = [...new Set(reported ?? [])].filter(
    (engine) => !isListed(engine) && foldedInto(engine) === null,
  );

  return [...ENGINE_ORDER, ...extra.sort(byEngineOrder)];
}
